import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PoundSterling, Save, RotateCcw, Info } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * SiteRatesPanel — writes the top-priority per-site rate overrides
 * (`pnl_ppa_p_per_kwh`, `pnl_export_p_per_kwh`, `pnl_grid_p_per_kwh`,
 * `pnl_admin_p_per_kwh`) plus `opex_per_kwp_gbp` directly to the project
 * doc, so ops can override the Investor Model / Quote values without
 * hopping to the Analytics dialog. Blank inputs delete the override and
 * the site P&L falls back through the resolution chain (site override →
 * investor model → quote → default).
 */
const FIELDS = [
  { key: "pnl_ppa_p_per_kwh",    label: "PPA rate",        suffix: "p/kWh", help: "Price the site's own tenant pays for self-consumed solar" },
  { key: "pnl_export_p_per_kwh", label: "Export rate",     suffix: "p/kWh", help: "Rate for kWh exported to the grid" },
  { key: "pnl_admin_p_per_kwh",  label: "Admin fee",       suffix: "p/kWh", help: "Per-kWh admin fee stacked on BTM energy" },
  { key: "pnl_grid_p_per_kwh",   label: "Grid alt-price",  suffix: "p/kWh", help: "Reference grid retail price used for benefit comparison" },
  { key: "opex_per_kwp_gbp",     label: "OpEx",            suffix: "£/kWp/yr", help: "Annual operating expense, pro-rated by installed kWp" },
];

export default function SiteRatesPanel({ project, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Seed the form from the project doc. Blank string means "no override".
  const seedFrom = React.useCallback((p) => {
    const out = {};
    for (const f of FIELDS) {
      const v = p?.[f.key];
      out[f.key] = v === null || v === undefined ? "" : String(v);
    }
    return out;
  }, []);

  useEffect(() => {
    setForm(seedFrom(project));
    setDirty(false);
  }, [project, seedFrom]);

  const upd = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const reset = () => {
    setForm(seedFrom(project));
    setDirty(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build a patch: empty string → null (deletes the override server-side),
      // non-empty → parsed float. Rejects non-numeric with a toast.
      const patch = {};
      for (const f of FIELDS) {
        const raw = (form[f.key] || "").trim();
        if (raw === "") { patch[f.key] = null; continue; }
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          toast.error(`${f.label} must be a non-negative number`);
          setSaving(false);
          return;
        }
        patch[f.key] = n;
      }
      await api.patch(`/projects/${project.id}`, patch);
      toast.success("Site rates saved — P&L will refresh");
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="site-rates-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="label flex items-center gap-2 mb-1">
            <PoundSterling size={12} /> Site Rates &amp; OpEx
          </div>
          <h2 className="font-display text-2xl font-semibold leading-tight">
            Override tariffs for this site
          </h2>
          <p className="muted text-sm mt-1 max-w-xl">
            Values here take priority over the Investor Model and the Quote.
            Leave a field blank to fall back through the chain
            <span className="font-mono text-xs muted"> (site override → investor model → quote → default)</span>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="label flex items-center gap-1.5 mb-1">
              {f.label}
              <span
                className="opacity-50 hover:opacity-100 transition-opacity"
                title={f.help}
              >
                <Info size={10} />
              </span>
            </label>
            <div className="flex items-stretch border border-[var(--line)] focus-within:border-[var(--accent)] transition-colors">
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-swiss !border-0 flex-1"
                placeholder="—"
                value={form[f.key] ?? ""}
                onChange={upd(f.key)}
                data-testid={`site-rates-${f.key}`}
              />
              <span className="px-3 flex items-center text-xs font-mono muted border-l border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                {f.suffix}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary inline-flex items-center gap-1.5"
          data-testid="site-rates-save"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save rates"}
        </button>
        <button
          onClick={reset}
          disabled={!dirty || saving}
          className="btn-ghost inline-flex items-center gap-1.5"
          data-testid="site-rates-reset"
        >
          <RotateCcw size={12} /> Reset
        </button>
        {dirty && <span className="text-xs muted ml-2">Unsaved changes</span>}
      </div>
    </div>
  );
}
